import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import menuPages from 'config/aiChat/menuPages';
import { useUnityActions } from './unityActions';
import { ModalContext } from './ModalProvider';
import menuModals from 'config/aiChat/menuModals';
import { useMinifiedMenu } from './useMinifiedMenu';
import settingsTab from 'config/aiChat/settingsTab';
import useUnity from './UseUnity';

interface AiChatContextType {
  aiChatShown: boolean;
  setAiChatShown: React.Dispatch<React.SetStateAction<boolean>>;
  setCommand: React.Dispatch<React.SetStateAction<CommandProps | null>>;
  noteInfo: { title: string, description: string } | null;
  setNoteInfo: React.Dispatch<React.SetStateAction<{ title: string, description: string } | null>>;
  messageHistory: string[][];
  setMessageHistory: React.Dispatch<React.SetStateAction<string[][]>>;
}

const AiChatContext = createContext<AiChatContextType | null>(null);

interface AiChatProviderProps {
  children: ReactNode;
}

interface CommandProps {
  name: string;
  args: string[];
  possible_values: string[];
  description: string;
  error: string | null;
}

export const AiChatProvider: React.FC<AiChatProviderProps> = ({ children }) => {
  const [aiChatShown, setAiChatShown] = useState(false);
  const [command, setCommand] = useState<CommandProps | null>(null);
  const { setRenderQuality, setScreenMode,
    setShow3DModel,
    setEditingMode,
    setCameraMode,
    measurementMode,
    setMeasurementMode,
    setCameraReset,
    setModelDensity,
    setWidgetSwitch,
    viewMode,
    setViewMode,
    setIdleCameraMode
  } = useUnityActions();
  const {
    addItemToPosition,
    removeMinifiedTab,
  } = useMinifiedMenu();
  const {
    setTimelineShown
  } = useUnity();
  const [noteInfo, setNoteInfo] = useState<{ title: string, description: string } | null>(null);
  const [messageHistory, setMessageHistory] = useState<string[][]>([]);
  const navigate = useNavigate();
  const { setModalType, setShowProjects, setShowKeyGuide, setInitialSettingsTab } = useContext(ModalContext);

  useEffect(() => {
    if (!aiChatShown) {
      removeMinifiedTab('aiChat', 'right');
    } else {
      addItemToPosition('bottomRight', { type: 'aiChat', data: null });
    }
  }, [aiChatShown]);

  useEffect(() => {
    if (command !== null) {
      const commandName = command.name.trim();
      const commandArguments = command.args.map((item) => item.trim());
      const argumentList = commandArguments.reduce<Record<string, string>>((acc, val, index, array) => {
        if (index % 2 === 0) {
          acc[val] = array[index + 1];
        }
        return acc;
      }, {});

      if (commandName === 'change_graphic') {
        const levelArg = argumentList["--level"];
        if (levelArg) {
          const possibleValues = [
            "low",
            "medium",
            "high",
            "ultra"
          ];
          const graphicsLevelIndex = possibleValues.indexOf(levelArg);
          if (graphicsLevelIndex !== -1) {
            setRenderQuality(graphicsLevelIndex);
            localStorage.setItem('renderQuality', graphicsLevelIndex.toString());
          } else {
            console.warn(`Unknown graphics level: ${levelArg}`);
          }
        }
      }
      if (commandName === 'change_screen_mode') {
        const screenModeArg = argumentList["--mode"];
        if (screenModeArg) {
          const possibleValues = [
            "fullscreen",
            "borderless",
            "window"
          ];
          const screenModeIndex = possibleValues.indexOf(screenModeArg);
          setScreenMode(screenModeIndex);
          localStorage.setItem('windowMode', screenModeIndex.toString());
        }
      }
      if (commandName === 'create_note') {
        if (window.vuplex) {
          window.vuplex.postMessage(JSON.stringify({
            object: 'MainNote',
            type: 'ProcessHub.DynamicNote.DynamicNoteController',
            method: 'SetNoteWithDetails',
            arguments: [JSON.stringify({ title: argumentList["--title"], description: argumentList["--content"] })]
          }));
        }
        setNoteInfo({ title: argumentList["--title"], description: argumentList["--content"] });
      }
      if (commandName === 'open_menu') {
        const menuToOpen = argumentList["--menu_id"];
        if (settingsTab[menuToOpen] !== undefined) {
          setInitialSettingsTab(settingsTab[menuToOpen]);
        }
        if (menuPages[menuToOpen]) {
          navigate(menuPages[menuToOpen]);
        }
        if (menuModals[menuToOpen]) {
          setModalType(menuModals[menuToOpen]);
        }
        if (menuToOpen === "projects") {
          setModalType("");
          setShowProjects(true);
        }
        if (menuToOpen === 'key_guide') {
          setShowKeyGuide(true);
        }
      }
      if (commandName === 'toggle') {
        const actionToToggle = argumentList["--item"];
        if (actionToToggle === 'scan_images') {
          setCameraMode((prev) => !prev);
        }
        if (actionToToggle === 'edit_mode') {
          setEditingMode((prev) => !prev);
        }
        if (actionToToggle === 'reset_camera') {
          setCameraReset(true);
        }
        if (actionToToggle === 'cinematic_camera') {
          setIdleCameraMode((prev) => !prev);
        }
        if (actionToToggle === 'cycle_camera') {
          if (viewMode === 2) {
            setViewMode(0);
          } else {
            setViewMode((prev) => prev + 1);
          }
        }
        if (actionToToggle === 'timeline') {
          setTimelineShown((prev: any) => !prev);
        }
        if (actionToToggle === 'key_guide') {
          setShowKeyGuide((prev: any) => !prev);
        }
        if (actionToToggle === 'measurement_tool') {
          if (measurementMode) {
            removeMinifiedTab('measurements', 'right');
          }
          else {
            addItemToPosition('bottomRight', { type: 'measurements', data: null });
          }
          setMeasurementMode((prev) => !prev);
        }
        if (actionToToggle === '3D_as_planned_model') {
          setShow3DModel((prev) => !prev);
        }
        if (actionToToggle === 'sparce_point_cloud') {
          setModelDensity(0);
        }
        if (actionToToggle === 'dense_point_cloud') {
          setModelDensity(1);
        }
        if (actionToToggle === '3DGS') {
          setModelDensity(2);
        }
        if (actionToToggle === 'widgets') {
          setWidgetSwitch((prev) => !prev);
        }
      }
    }
  }, [command, window]);

  return (
    <AiChatContext.Provider value={{ aiChatShown, setAiChatShown, setCommand, noteInfo, setNoteInfo, messageHistory, setMessageHistory }}>
      {children}
    </AiChatContext.Provider>
  );
};

// Custom hook to use the AiChat context
export const useAiChat = (): AiChatContextType => {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error('useAiChat must be used within an AiChatProvider');
  }
  return context;
};
